import React from "react";
import { useSEMasters } from "@/modules/common/hooks/useSEMasters";
import { OrderItemResponse } from "@/api";

export interface OrderItemsTableProps {
  items: OrderItemResponse[];

  /**
   * Show the richer financial columns.
   * Defaults to true.
   */
  showFinancials?: boolean;

  /**
   * When provided, an Actions column is rendered.
   */
  onEdit?: (index: number) => void;
}

export default function OrderItemsTable({
  items = [],
  showFinancials = true,
  onEdit,
}: OrderItemsTableProps) {
  const { applications } = useSEMasters();

  const application = (applicationId?: number) =>
    applications?.find(
      (application) =>
        Number(application.id) ===
        Number(applicationId),
    );

  const applicationLabel = (
    applicationId?: number,
  ) => {
    if (!applicationId) {
      return "-";
    }

    return (
      application(applicationId)?.service_name ??
      `Application #${applicationId}`
    );
  };

  const applicationCode = (
    applicationId?: number,
  ) => {
    if (!applicationId) {
      return "-";
    }

    return (
      application(applicationId)?.service_code ??
      "-"
    );
  };

  const money = (value?: number) =>
    `₹${Number(value ?? 0).toLocaleString(
      "en-IN",
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    )}`;

  const totals = items.reduce(
    (acc, item) => {
      acc.quantity += Number(
        item.quantity ?? 0,
      );

      acc.discount += Number(
        item.discount_amount ?? 0,
      );

      acc.taxable += Number(
        item.taxable_amount ?? 0,
      );

      acc.total += Number(
        item.total_amount ?? 0,
      );

      return acc;
    },
    {
      quantity: 0,
      discount: 0,
      taxable: 0,
      total: 0,
    },
  );

  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed p-6 text-center text-sm text-gray-500">
        No services have been added.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr className="border-b bg-gray-50">
            <th className="px-4 py-3 text-left font-medium">
              Service
            </th>

            <th className="px-4 py-3 text-left font-medium">
              Code / HSN
            </th>

            <th className="px-4 py-3 text-right font-medium">
              Qty
            </th>

            {showFinancials && (
              <>
                <th className="px-4 py-3 text-right font-medium">
                  Unit Price
                </th>

                <th className="px-4 py-3 text-right font-medium">
                  Discount
                </th>

                <th className="px-4 py-3 text-right font-medium">
                  Taxable
                </th>

                <th className="px-4 py-3 text-right font-medium">
                  GST %
                </th>

                <th className="px-4 py-3 text-right font-medium">
                  Total
                </th>
              </>
            )}

            {onEdit && (
              <th className="px-4 py-3 text-right font-medium">
                Actions
              </th>
            )}
          </tr>
        </thead>

        <tbody>
          {items.map((item, index) => (
            <tr
              key={
                item.id ??
                `${item.application_id}-${index}`
              }
              className="border-b last:border-0"
            >
              {/* Service */}
              <td className="px-4 py-3">
                <div className="font-medium">
                  {
                    item.application?.service_name  ?? ""
                  }
                </div>

                {item.description && (
                  <div className="mt-1 text-xs text-gray-500">
                    {item.description}
                  </div>
                )}
              </td>

              {/* Code / HSN */}
              <td className="px-4 py-3">
                <div>
                  
                  {
                    item.application?.id
                  }
                </div>

                {item.hsn_sac && (
                  <div className="mt-1 text-xs text-gray-500">
                    HSN/SAC: {item.hsn_sac}
                  </div>
                )}
              </td>

              {/* Quantity */}
              <td className="px-4 py-3 text-right">
                {item.quantity ?? 0}
              </td>

              {showFinancials && (
                <>
                  {/* Unit Price */}
                  <td className="px-4 py-3 text-right">
                    {money(Number(item.unit_price))}
                  </td>

                  {/* Discount */}
                  <td className="px-4 py-3 text-right">
                    <div>
                      {item.discount_percentage ??
                        0}
                      %
                    </div>

                    <div className="text-xs text-gray-500">
                      {money(
                        Number(item.discount_amount),
                      )}
                    </div>
                  </td>

                  {/* Taxable */}
                  <td className="px-4 py-3 text-right">
                    {money(
                      Number(item.taxable_amount),
                    )}
                  </td>

                  {/* GST */}
                  <td className="px-4 py-3 text-right">
                    {item.tax_percentage ?? 0}%
                  </td>

                  {/* Total */}
                  <td className="px-4 py-3 text-right font-semibold">
                    {money(Number(item.total_amount))}
                  </td>
                </>
              )}

              {/* Actions */}
              {onEdit && (
                <td className="px-4 py-3 text-right">
                  <button
                    type="button"
                    onClick={() =>
                      onEdit(index)
                    }
                    className="rounded-md border px-3 py-2 text-sm"
                  >
                    Edit
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>

        {showFinancials && (
          <tfoot>
            <tr className="border-t-2 bg-gray-50 font-semibold">
              {/* Service + Code */}
              <td
                colSpan={2}
                className="px-4 py-4"
              >
                Totals
              </td>

              {/* Quantity */}
              <td className="px-4 py-4 text-right">
                {totals.quantity}
              </td>

              {/* Unit Price */}
              <td className="px-4 py-4 text-right">
                —
              </td>

              {/* Discount */}
              <td className="px-4 py-4 text-right">
                {money(totals.discount)}
              </td>

              {/* Taxable */}
              <td className="px-4 py-4 text-right">
                {money(totals.taxable)}
              </td>

              {/* GST */}
              <td className="px-4 py-4 text-right">
                —
              </td>

              {/* Total */}
              <td className="px-4 py-4 text-right text-base">
                {money(totals.total)}
              </td>

              {onEdit && (
                <td />
              )}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}